import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { View } from '../types';
import { Capacitor } from '@capacitor/core';

// Este contexto se llamaba "Tutorial" porque originalmente combinaba el
// estado de navegación (currentView/setCurrentView, usado en toda la app)
// con un sistema de tooltips guiados que se mostraba solo en el primer
// ingreso a cada pantalla. Ese tutorial se quitó (se consideró innecesario),
// pero el nombre del contexto se mantiene tal cual porque decenas de
// pantallas ya lo consumen vía useTutorial() solo por la parte de
// navegación — renombrarlo no aporta nada y arriesga romper imports.
interface TutorialContextType {
  currentView: View;
  setCurrentView: (view: View) => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const TutorialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentView, setCurrentViewState] = useState<View>(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    const pathname = window.location.pathname.toLowerCase();

    if (pathname.startsWith('/p/')) return View.PUBLIC_PROFILE;
    if (pathname.startsWith('/found/')) return View.FOUND_MODE;
    if (pathname === '/features' || pathname === '/features/') return View.FEATURES;

    // @ts-ignore
    if (viewParam && Object.values(View).includes(viewParam)) {
      return viewParam as View;
    }
    // Default to LOGIN for native app, LANDING for web
    const isActualNative = Capacitor.isNativePlatform();
    return isActualNative ? View.LOGIN : View.LANDING;
  });

  // Sync state with URL changes (Back/Forward buttons)
  useEffect(() => {
    const pathname = location.pathname;
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');

    if (pathname.startsWith('/p/')) {
      if (currentView !== View.PUBLIC_PROFILE) setCurrentViewState(View.PUBLIC_PROFILE);
    } else if (pathname.startsWith('/found/')) {
      if (currentView !== View.FOUND_MODE) setCurrentViewState(View.FOUND_MODE);
    } else if (pathname === '/features') {
      if (currentView !== View.FEATURES) setCurrentViewState(View.FEATURES);
    } else if (viewParam && Object.values(View).includes(viewParam as View)) {
      if (currentView !== viewParam) setCurrentViewState(viewParam as View);
    }
  }, [location.pathname, location.search]);

  const setCurrentView = (view: View) => {
    if (view === currentView) return;

    setCurrentViewState(view);

    // Update URL via Router
    if (view === View.PUBLIC_PROFILE) {
      // Don't navigate here, public profiles are opened via /p/ID from the caller
    } else if (view === View.FOUND_MODE) {
      // Same for found mode
    } else if (view === View.FEATURES) {
      navigate('/features');
    } else if (view === View.LANDING) {
      navigate('/', { replace: true });
    } else {
      // Legacy support for other views via query param
      navigate(`/?view=${view}`);
    }
  };

  return (
    <TutorialContext.Provider value={{ currentView, setCurrentView }}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
